import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = () => `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/placement`;

export type PlacementTab =
    | 'data'
    | 'summary'
    | 'insights'
    | 'ahli'
    | 'spm'
    | 'reconsideration'
    | 'focus'
    | 'insights-dashboard'; // legacy alias → summary
export type FileType = 'ANALYSIS' | 'ACTIONABLES' | 'INSIGHT_DASHBOARD';
export type TemplateType = 'B_SCHOOL' | 'ENGINEERING' | 'LAW_SCHOOL';
export type MatrixType = 'AHLI' | 'SPM';

export interface PlacementListCriteria {
    search?: string;
    instituteGroupId?: string | string[];
    programLevel?: string;
    dataUpdated?: 'ALL' | 'UPLOADED' | 'NOT_UPLOADED';
    lastUpdatedFrom?: string;
    lastUpdatedTo?: string;
    limit?: number;
    offset?: number;
}

export interface PlacementInstituteRow {
    id: number;
    name: string;
    location?: string | null;
    cityCode?: string | null;
    clientType?: string | null;
    instituteGroupId?: string | null;
    dataStatus: string;
    analysisStatus: string;
    lastUpdated?: string | null;
    moduleEnabled?: number | boolean;
    templateType?: TemplateType | null;
    /** True when a SUCCESS INSIGHT_DASHBOARD upload exists (Summary tab has data). */
    hasSummaryData?: boolean;
}

class PlacementAnalyticsService {
    static list(criteria: PlacementListCriteria = {}) {
        return axiosClient().post(`${BASE()}/list`, criteria);
    }

    static listCount(criteria: PlacementListCriteria = {}) {
        return axiosClient().post(`${BASE()}/list/count`, criteria);
    }

    static upsertSettings(
        instituteId: number | string,
        body: { templateType?: TemplateType | null; moduleEnabled?: boolean | number }
    ) {
        return axiosClient().post(`${BASE()}/${instituteId}/settings`, body);
    }

    static upload(
        instituteId: number | string,
        file: File,
        body: { fileType: FileType; yearFrom?: number | string; yearTo?: number | string; templateType?: TemplateType }
    ) {
        const form = new FormData();
        form.append('file', file);
        form.append('fileType', body.fileType);
        if (body.yearFrom != null && body.yearFrom !== '') form.append('yearFrom', String(body.yearFrom));
        if (body.yearTo != null && body.yearTo !== '') form.append('yearTo', String(body.yearTo));
        if (body.templateType) form.append('templateType', body.templateType);
        return axiosClient().post(`${BASE()}/${instituteId}/upload`, form);
    }

    static downloadUploadUrl(instituteId: number | string, uploadId: number | string) {
        return `${BASE()}/${instituteId}/upload/${uploadId}/download`;
    }

    static async downloadUpload(instituteId: number | string, uploadId: number | string, fileName?: string) {
        const res = await axiosClient().get(this.downloadUploadUrl(instituteId, uploadId), {
            responseType: 'blob',
        });
        const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `placement-upload-${uploadId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    static getAxisConfig(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/axis-config`);
    }

    static getMeta(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/meta`);
    }

    static upsertAxisConfig(
        instituteId: number | string,
        body: {
            placementProgramId?: number | null;
            matrixType: MatrixType;
            xThreshold: number;
            xMax: number;
            yMax: number;
            yThreshold: number;
        }
    ) {
        return axiosClient().post(`${BASE()}/${instituteId}/axis-config`, body);
    }

    static getInsights(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/insights`);
    }

    static getInsightDashboard(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/insight-dashboard`);
    }

    static getAhliMatrix(instituteId: number | string, programId?: number | null) {
        return axiosClient().get(`${BASE()}/${instituteId}/ahli-matrix`, {
            params: programId != null ? { programId } : {},
        });
    }

    static getSpmMatrix(instituteId: number | string, programId?: number | null) {
        return axiosClient().get(`${BASE()}/${instituteId}/spm-matrix`, {
            params: programId != null ? { programId } : {},
        });
    }

    static getActionables(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/actionables`);
    }

    static focusCompany(
        instituteId: number | string,
        body: {
            companyId: number;
            action: 'ADD' | 'REMOVE';
            leakType?: string;
            comment?: string;
            isSystemSuggested?: boolean;
        }
    ) {
        return axiosClient().post(`${BASE()}/${instituteId}/focus-company`, body);
    }

    static getFocusList(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/focus-list`);
    }

    static async exportFocusList(instituteId: number | string) {
        const res = await axiosClient().get(`${BASE()}/${instituteId}/focus-list/export`, {
            responseType: 'blob',
        });
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `placement-focus-${instituteId}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    static companySearch(instituteId: number | string, q: string) {
        return axiosClient().get(`${BASE()}/company-search`, { params: { instituteId, q } });
    }

    static getReconsideration(instituteId: number | string) {
        return axiosClient().get(`${BASE()}/${instituteId}/reconsideration`);
    }

    static emptyListCriteria(): PlacementListCriteria {
        return {
            search: '',
            dataUpdated: 'ALL',
            limit: 50,
            offset: 0,
        };
    }
}

export default PlacementAnalyticsService;
